/* LEGEND — sign-in screen. Design ported from login.html; sign-in is Firebase Google auth. */
import React from 'react';
import { signInWithGoogle } from './lib/firebase';
import './styles/login.css';

const { useState, useEffect, useRef } = React;

const GoogleMark = () =>
  React.createElement('svg', { viewBox: '0 0 48 48', 'aria-hidden': true },
    React.createElement('path', { fill: '#EA4335', d: 'M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z' }),
    React.createElement('path', { fill: '#4285F4', d: 'M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z' }),
    React.createElement('path', { fill: '#FBBC05', d: 'M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z' }),
    React.createElement('path', { fill: '#34A853', d: 'M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z' }));

function Motif() {
  const ref = useRef<any>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    function draw() {
      const p = cv.parentElement, dpr = window.devicePixelRatio || 1;
      const w = p.clientWidth, h = p.clientHeight;
      cv.width = w * dpr; cv.height = h * dpr; cv.style.width = w + 'px'; cv.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, w, h);
      let x = 8, price = h * 0.62, seed = 7;
      const n = Math.ceil(w / 26) + 2;
      const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
      for (let i = 0; i < n; i++) {
        const o = price, mv = (rnd() - 0.46) * 34; price = Math.max(h * 0.18, Math.min(h * 0.84, price + mv));
        const c = price, up = c <= o;
        const bodyTop = Math.min(o, c), bodyH = Math.max(4, Math.abs(c - o));
        const wickH = bodyH + 10 + rnd() * 26, wickTop = bodyTop - (wickH - bodyH) / 2;
        const col = up ? 'rgba(56,192,138,' : 'rgba(236,106,106,';
        ctx.strokeStyle = col + '0.45)'; ctx.lineWidth = 1.4;
        ctx.beginPath(); ctx.moveTo(x + 5, wickTop); ctx.lineTo(x + 5, wickTop + wickH); ctx.stroke();
        ctx.fillStyle = col + (up ? '0.34)' : '0.30)');
        ctx.fillRect(x, bodyTop, 10, bodyH);
        x += 26;
      }
    }
    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, []);
  return React.createElement('canvas', { className: 'motif', ref });
}

export function Login() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setErr(null); setLoading(true);
    try {
      await signInWithGoogle();
      // onAuthChange in the gate swaps to the Terminal.
    } catch (e: any) {
      setLoading(false);
      setErr(e?.message || 'Sign-in failed');
    }
  }

  return React.createElement('div', { className: 'login-wrap' },
    React.createElement('div', { className: 'brand-panel' },
      React.createElement(Motif),
      React.createElement('div', { className: 'bp-top' },
        React.createElement('div', { className: 'bp-mark' }, React.createElement('span', null, 'M')),
        React.createElement('div', { className: 'bp-word' }, 'MOMENTUM', React.createElement('span', { className: 'sub' }, 'TRADING TERMINAL'))),
      React.createElement('div', { className: 'bp-mid' },
        React.createElement('div', { className: 'bp-head' }, 'Trade the ', React.createElement('span', { className: 'accent' }, 'move'), ',', React.createElement('br'), 'not the noise.'),
        React.createElement('div', { className: 'bp-sub' }, 'On-chart orders, multi-timeframe support & resistance, and live position management — all in one fast, focused desk.'),
        React.createElement('div', { className: 'bp-stats' },
          React.createElement('div', { className: 'bp-stat' }, React.createElement('div', { className: 'v num' }, '12ms'), React.createElement('div', { className: 'k' }, 'Order routing')),
          React.createElement('div', { className: 'bp-stat' }, React.createElement('div', { className: 'v num up' }, '98.4%'), React.createElement('div', { className: 'k' }, 'Fill quality')),
          React.createElement('div', { className: 'bp-stat' }, React.createElement('div', { className: 'v num' }, '24/6'), React.createElement('div', { className: 'k' }, 'Market hours')))),
      React.createElement('div', { className: 'bp-foot' }, '© Momentum — simulated environment')),

    React.createElement('div', { className: 'login-side' },
      React.createElement('div', { className: 'login-card' },
        React.createElement('div', { className: 'lc-mobile-brand' }, React.createElement('div', { className: 'm' }, 'M'), React.createElement('div', { className: 'bp-word', style: { fontSize: 16 } }, 'MOMENTUM')),
        React.createElement('div', { className: 'lc-eyebrow' }, 'Welcome back'),
        React.createElement('div', { className: 'lc-title' }, 'Sign in to your desk'),
        React.createElement('div', { className: 'lc-desc' }, 'Use your Google account to access the Momentum terminal. Your watchlists and open positions are waiting.'),
        React.createElement('button', { className: 'gbtn' + (loading ? ' loading' : ''), onClick: go, disabled: loading },
          loading ? React.createElement('span', { className: 'spin' }) : React.createElement(GoogleMark),
          React.createElement('span', { className: 'gbtn-label' }, loading ? 'Signing you in…' : 'Continue with Google')),
        err ? React.createElement('div', { className: 'lc-err' }, err) : null,
        React.createElement('div', { className: 'lc-legal' }, React.createElement('span', { className: 'dot-live' }), ' Secured session · paper trading'))));
}
